import { AsyncPipe } from '@angular/common'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatTooltip } from '@angular/material/tooltip'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { setSeconds } from 'date-fns'
import { FilesizePipe } from '../filesize.pipe'
import { combineLatest, filter, map, take, tap } from 'rxjs'
import { callResourceTypesToUpload } from '../call-document-upload/call-document-upload.component'
import { CallsService, MinutesStyling, UploadCallData } from '../calls.service'
import { DatetimePickerComponent } from '../common/datetime-picker/datetime-picker.component'
import { FileUploadComponent } from '../file-upload/file-upload.component'
import { LoggerService } from '../logger.service'
import { MinutesStylingComponent } from '../minutes-styling/minutes-styling.component'
import { getMinuteStylesOfOrganization } from '../organizations.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToastService } from '../toast.service'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { UploadsService } from '../uploads.service'
import { SelectionComponent } from '../users-selection/selection.component'
import { User, UsersService } from '../users.service'
import { constructNameForUser } from '../utils'

@Component({
  selector: 'app-upload-call-page',
  standalone: true,
  imports: [
    FormsModule,
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    TranslateModule,
    AsyncPipe,
    SpinnerComponent,
    MatDatepickerModule,
    MatIcon,
    MatIconButton,
    MatTooltip,
    FilesizePipe,
    SelectionComponent,
    MinutesStylingComponent,
    ToolbarComponent,
    FileUploadComponent,
    MatProgressBarModule,
    DatetimePickerComponent,
  ],
  templateUrl: './upload-call-page.component.html',
  styleUrl: './upload-call-page.component.scss',
})
export class UploadCallPageComponent implements OnInit {
  private changeDetectorRef = inject(ChangeDetectorRef)
  callsService = inject(CallsService)
  loggerService = inject(LoggerService)
  uploadCallMutation = this.callsService.uploadCall()
  uploadProgress = 0
  toastService = inject(ToastService)
  translateService = inject(TranslateService)
  router = inject(Router)
  route = inject(ActivatedRoute)
  http = inject(HttpClient)
  uploadsService = inject(UploadsService)
  usersService = inject(UsersService)
  currentUserQuery$ = this.usersService.getCurrentUser()
  acceptedFileTypes = ['mp3', 'mp4', 'wav']
  acceptedFileTypesString = this.acceptedFileTypes.map((type) => `.${type}`).join(',')

  uploading = false

  currentUser$ = this.usersService.currentUser$.pipe(
    take(1),
    tap((user) => {
      if (user === null || this.data !== null) {
        return
      }
      this.data = {
        name: '',
        location: '',
        fileExtension: null,
        uploadKey: null,
        date: setSeconds(new Date(), 0),
        attendees: [],
        userIds: [],
        customDocumentUploadInfo: null,
        agendaUploadInfo: null,
        agendaType: null,
        ...getMinuteStylesOfOrganization(user.organization),
      }
    })
  )

  data: UploadCallData | null = null
  chosenFile: File | null = null
  organizationUsers$ = this.usersService.getOrganizationUsers()

  organizationUsersWithoutCurrentUser$ = combineLatest([this.currentUserQuery$, this.organizationUsers$]).pipe(
    map(([currentUserResult, organizationUsersResult]) => {
      if (currentUserResult.isSuccess && organizationUsersResult.isSuccess) {
        const currentUser = currentUserResult.data
        const filteredUsers = organizationUsersResult.data!.filter((user) => user.id !== currentUser!.id)
        return { isSuccess: true, data: filteredUsers }
      } else {
        return { isSuccess: false, data: [] }
      }
    })
  )

  time: string | null = null

  get customDocumentExtensions(): string[] {
    return callResourceTypesToUpload['CUSTOM_DOCUMENT'].extensions
  }

  get customDocumentMaxSize(): number | null {
    return callResourceTypesToUpload['CUSTOM_DOCUMENT'].maxSize
  }

  get agendaExtensions(): string[] {
    return callResourceTypesToUpload['AGENDA'].extensions
  }

  get agendaMaxSize(): number | null {
    return callResourceTypesToUpload['AGENDA'].maxSize
  }

  ngOnInit() {
    this.currentUser$.subscribe()

    this.uploadCallMutation.result$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((call) => {
        this.toastService.show('UPLOAD_CALL.IMPORT_SUCCESSFUL_NOTIFICATION')
        this.router.navigate(['/app', 'meetings', call!.id])
      })
  }

  addFullNameToUserList(users: User[]) {
    return users.map((user) => {
      return {
        ...user,
        fullName: constructNameForUser(user),
      }
    })
  }

  uploadCall() {
    this.uploadCallMutation.mutate(this.data!)
  }

  fileInputChanged(fileInputEvent: any) {
    this.chosenFile = fileInputEvent.target.files[0]
    const extension = this.chosenFile!.name.split('.').pop()!.toLowerCase()
    if (this.chosenFile !== null && !this.acceptedFileTypes.includes(extension)) {
      const fileTypes = this.acceptedFileTypes.map((type) => type.toUpperCase()).join(', ')
      this.toastService.showRaw(this.translateService.instant('CALLS.CALL_INFO.DOCUMENT_UPLOAD.INVALID_FILE_TYPE', { fileTypes }))
      this.chosenFile = null
      return
    }
  }

  onUserSelectionChange(users: User[]) {
    this.data!.userIds = users.map((user) => user.id)
  }

  upload() {
    if (this.chosenFile === null) {
      return
    }
    this.uploading = true
    this.uploadsService.uploadRecording(this.chosenFile).subscribe({
      next: (uploadInfo) => {
        this.uploadProgress = uploadInfo.progress
        if (uploadInfo.uploadKey !== null) {
          this.uploading = false
          this.data!.uploadKey = uploadInfo.uploadKey
          this.data!.fileExtension = this.chosenFile?.name?.split('.')?.pop()?.toUpperCase() ?? null
        }
        this.changeDetectorRef.detectChanges()
      },
      error: (error: HttpErrorResponse) => {
        this.uploading = false
        this.uploadProgress = 0
        if (error.status === 0) {
          this.toastService.show('UPLOAD_CALL.FAILURE.NETWORK_FAILURE')
        } else {
          this.toastService.show('UPLOAD_CALL.FAILURE.UNKNOWN_FAILURE')
        }
        this.loggerService.error('Error uploading file', error)
      },
    })
  }

  updateMinutesStyling(minutesStyling: Partial<MinutesStyling>) {
    this.data = { ...this.data!, ...minutesStyling }
  }
}
